import Alert, { AlertType } from '@components/Alert';
import { useAppMessages } from '@effects/useAppMessages';
import { IAppMessageCriteria } from '@typings';
import { resolveMessageText } from '@utils/app-message-matcher';
import { MessageType } from '@utils/enums';
import React from 'react';

interface Props {
    criteria: IAppMessageCriteria;
}

const LEVEL_TO_ALERT_TYPE: Record<MessageType, AlertType> = {
    [MessageType.Info]: AlertType.Info,
    [MessageType.Warning]: AlertType.Warn,
    [MessageType.Error]: AlertType.Error,
    [MessageType.Success]: AlertType.Success,
};

export const toAlertType = (level: MessageType): AlertType => LEVEL_TO_ALERT_TYPE[level] ?? AlertType.Info;

/**
 * Renders the `App_Messages` rows that match the given screen, each through the app's existing
 * `Alert` component. Glue only: no styling of its own beyond honouring editor line breaks, and no
 * store access (the context is built by the page). Text is rendered as plain strings.
 */
const AppMessages: React.FC<Props> = ({ criteria }) => {
    const messages = useAppMessages(criteria);
    const renderable = messages
        .map((message) => ({ message, text: resolveMessageText(message) }))
        .filter(({ text }) => text !== '');

    if (renderable.length === 0) {
        return null;
    }

    return (
        <div
            className="AppMessages"
            data-testid="app-messages"
        >
            <style jsx>{`
                .AppMessages__text {
                    white-space: pre-line;
                }
            `}</style>
            {renderable.map(({ message, text }) => (
                <Alert
                    key={message.id}
                    type={toAlertType(message.level)}
                    title={message.title ?? undefined}
                    subtitle={<span className="AppMessages__text">{text}</span>}
                    fullWidth
                />
            ))}
        </div>
    );
};

export default AppMessages;
